import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { Table } from "react-bootstrap";

const utahData = "/img/headerlogo.png";
const bodex = "/img/bodexLogo.png";
const kpi = "/img/KPI-DASH 2.png";
const pdfIcon = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

export default function CsAndItTable3({ data }) {
  const pdfRef = useRef();

  if (!data || data.length === 0) return <p>No data available</p>;

  const years = data.map((item) => item.year);
  const maleCounts = data.map((item) => parseInt(item.male));
  const femaleCounts = data.map((item) => parseInt(item.female));
  const ratios = data.map((item) => parseFloat(item.male_to_female_ratio));

  // Summary Stats
  const highestRatio = Math.max(...ratios).toFixed(2);
  const lowestRatio = Math.min(...ratios).toFixed(2);
  const avgRatio = (ratios.reduce((a, b) => a + b, 0) / ratios.length).toFixed(
    2
  );

  const peakYear = years[ratios.indexOf(Math.max(...ratios))];
  const minYear = years[ratios.indexOf(Math.min(...ratios))];

  const chartData = {
    labels: years,
    datasets: [
      {
        label: "Male Employment",
        data: maleCounts,
        backgroundColor: "#0ea5e980",
        borderColor: "#0ea5e9",
        borderWidth: 1,
        type: "bar",
      },
      {
        label: "Female Employment",
        data: femaleCounts,
        backgroundColor: "#EC4899",
        borderColor: "#EC489980",
        borderWidth: 1,
        type: "bar",
      },
      {
        label: "Male-to-Female Ratio",
        data: ratios,
        borderColor: "#CCFFFF",
        backgroundColor: "#CCFFFF40",
        type: "line",
        borderWidth: 3,
        pointRadius: 6,
        yAxisID: "ratioAxis",
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "Gender Employment Analysis & Ratio (2018–2024)",
        font: { size: 16, weight: "bold" },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: { display: true, text: "Employment Count", font: { size: 14, weight: "bold" } },
      },
      ratioAxis: {
        position: "right",
        beginAtZero: false,
        title: { display: true, text: "Male-to-Female Ratio", font: { size: 14, weight: "bold" } },
        grid: { drawOnChartArea: false },
      },
      x: {
        title: {
          display: true,
          text: "Year",
          font: { size: 14, weight: "bold" },
        },
      },
    },
  };

  // ---------------- PDF Export ----------------
    const downloadPDF = () => {
      const input = pdfRef.current;
      html2canvas(input, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        scrollY: -window.scrollY,
      }).then((canvas) => {
        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF("p", "mm", "a4");
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const imgWidth = pageWidth - 10;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        let heightLeft = imgHeight;
        let position = 0;
        let pageNumber = 1;

        const addHeader = () => {
          pdf.addImage(utahData, "PNG", 60, 10, 32, 16);
          pdf.setFontSize(26);
          pdf.text("UTahData.org", 95, 22);
          pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
          pdf.line(10, 35, 200, 35);
        };

        const addFooter = (pageNumber) => {
          pdf.setFontSize(9);
          const footerY = pageHeight - 10;
          pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
          pdf.text(
            `Downloaded on: ${new Date().toLocaleDateString()}`,
            10,
            footerY
          );
          pdf.text(
            `Page ${pageNumber}`,
            pageWidth - 10,
            footerY,
            null,
            null,
            "right"
          );
        };

        // First page
        addHeader();
        pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
        heightLeft -= pageHeight - 32;

        // Add more pages if needed
        while (heightLeft > 0) {
          pdf.addPage();
          pageNumber++;
          addHeader();
          position = heightLeft - imgHeight;
          pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
          heightLeft -= pageHeight - 32;
          addFooter(pageNumber);
        }

        // Final footer
        pdf.line(10, 260, 200, 260);
        const footerY = 265;
        pdf.addImage(utahData, "PNG", 22, footerY, 30, 15);
        pdf.setFontSize(10);
        pdf.text("Built by", 55, footerY + 10);
        pdf.addImage(bodex, "PNG", 68, footerY + 4, 44, 10);
        pdf.text("using", 112, footerY + 10);
        pdf.addImage(kpi, "PNG", 125, footerY, 20, 20);
        pdf.text("20250916.v3.198", 148, footerY + 10);
        pdf.text(`Downloaded on: ${new Date().toLocaleDateString()}`, 150, 295);
        pdf.text(`From: ${window.location.href}`, 10, 295);

        pdf.save("cs_it_gender_ratio.pdf");
      });
    };

  // ---------------- Render ----------------
  return (
    <>
      <div ref={pdfRef} className="p-6 bg-white rounded-xl shadow-md">
        <h2 className="text-2xl font-bold text-center mb-6 text-gray-800">
          Gender Employment Comparison & Ratio Trend (2018–2024)
        </h2>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8 text-center">
          <div className="p-4 bg-blue-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">Highest Ratio</p>
            <p className="text-xl font-bold text-blue-900">
              {highestRatio} ({peakYear})
            </p>
          </div>

          <div className="p-4 bg-red-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">Lowest Ratio</p>
            <p className="text-xl font-bold text-red-900">
              {lowestRatio} ({minYear})
            </p>
          </div>

          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">Average Ratio</p>
            <p className="text-xl font-bold text-green-900">{avgRatio}</p>
          </div>
        </div>

        {/* Table + Chart */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
          {/* Table */}
          <div className="flex flex-col justify-center">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden">
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="p-2 text-left">Year</th>
                    <th className="p-2" style={{ textAlign: 'right' }}>Male Employment</th>
                    <th className="p-2" style={{ textAlign: 'right' }}>Female Employment</th>
                    <th className="p-2" style={{ textAlign: 'right' }}>Male to Female Ratio</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((row, i) => (
                    <tr key={i} className="hover:bg-gray-100 transition p-2">
                      <td className="p-2 text-left">{row.year}</td>
                      <td className="p-2" style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                        {Number(row.male).toLocaleString("en-US")}
                      </td>
                      <td className="p-2" style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                        {Number(row.female).toLocaleString("en-US")}
                      </td>
                      <td
                        className={`p-2 font-bold ${
                          row.male_to_female_ratio > 2
                            ? "text-red-600"
                            : "text-blue-700"
                        }`}
                        style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
                      >
                        {row.male_to_female_ratio}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[400px] w-full h-96 p-4">
              <Bar data={chartData} options={options} />
            </div>
          </div>
        </div>
      </div>

      {/* Download Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 text-white px-4 py-2 rounded-lg flex items-center gap-2 shadow-md hover:scale-105 transition"
        >
          Download as PDF
          <img src={pdfIcon} className="w-[1.5rem]" alt="Download PDF" />
        </button>
      </div>
    </>
  );
}
